<template>
	<div>
		<!-- customer-dialog is declared here to use it as ref -->
		<customer-dialog
			ref="CustomerDialog"
			@match-selected="$refs.VisitDialog.entityId = $event"
		/>

		<!-- visit dialog is declared here to use it as ref -->
		<visit-dialog
			ref="VisitDialog"
			@on-save="fetchVisits"
			@update-balance="updateBalance"
			@add-customer="e => $refs.CustomerDialog.addCustomer({ name: e.name, regionId: e.regionId })"
		/>

		<!-- add-visit bottom sheet is declared here to use it as ref -->
		<add-visit-bottom-sheet ref="AddVisitBottomSheet" />

		<!-- delete dialog declaered here to use it as ref -->
		<delete-dialog
			ref="DeleteDialog"
			@on-delete="
				fetchVisits();
				updateBalance();
			"
		>
			<template v-slot:default>
				<p
					class="mb-0 text-body-1"
					v-html="
						$T('messages.are-you-sure-you-want-to-delete-$', {
							text: $T('the-visit'),
						})
					"
				/>
			</template>
		</delete-dialog>

		<view-visit-sheet ref="ViewVisitSheet" />
		<!-- {{ serverTime }} -->
		<!-- add visit -->
		<v-fab-transition>
			<v-btn
				v-if="(date === moment().format('YYYY-MM-DD')) || allowAdd"
				color="primary"
				:class="['add-fab', { 'sale-system-add-fab': usePrices }]"
				fab
				fixed
				bottom
				:right="$i18n.locale !== 'ar'"
				:left="$i18n.locale === 'ar'"
				v-show="isVisibleAdd"
				@click="$refs.AddVisitBottomSheet.open(selectedFilter, date)"
			>
				<v-icon>mdi-plus</v-icon>
			</v-btn>
		</v-fab-transition>

		<!-- daily report -->
		<v-bottom-sheet v-model="dailyReportSheet" content-class="rounded-t-lg">
			<v-form @submit.prevent="submitDailyReport">
				<!-- card -->
				<v-card class="rounded-t-lg">
					<!-- title -->
					<v-toolbar dark flat color="primary" class="text-h6 text-uppercase">
						<v-spacer />
						{{ $T("daily-report") }}
						<v-spacer />
					</v-toolbar>
					<v-card-text>
						<v-radio-group
							v-model="dailyReportData.classification"
							row
							dense
							hide-details
							class="mt-0"
						>
							<v-radio
								:label="$T('report-type.important')"
								:value="dailyReportClassification.important"
							/>
							<v-radio
								:label="$T('report-type.complaint')"
								:value="dailyReportClassification.complaint"
							/>
							<v-radio
								:label="$T('report-type.normal-note')"
								:value="dailyReportClassification.normalNote"
							/>
						</v-radio-group>
						<v-textarea
							v-model="dailyReportData.report"
							:label="$T('inputs.report')"
							hide-details
							auto-grow
							rows="3"
						/>
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn
							color="success"
							text
							type="submit"
							:loading="dailyReportLoading"
							:disabled="dailyReportLoading"
						>
							{{ $T("save") }}
						</v-btn>
						<v-btn
							color="grey darken-1"
							text
							:disabled="dailyReportLoading"
							@click="dailyReportSheet = false"
						>
							{{ $T("close") }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-bottom-sheet>

		<!-- region visits rate -->
		<region-visits-rate-card
			v-if="regionPlansStatisticsSummary.totalEntities"
			:percentage="regionPlansStatisticsSummary.percentage"
			:visited-count="regionPlansStatisticsSummary.visitedCount"
			:remaining-count="regionPlansStatisticsSummary.remainingCount"
			show-details-link
			class="mb-0 mx-3 mt-3"
			@details="$router.push({ name: 'region-plans-statistics' })"
		/>

		<!-- filter -->
		<v-card flat color="transparent" class="px-0">
			<v-card-text class="d-flex flex-wrap justify-space-between align-center px-0 py-2">
				<!-- not translated -->
				<!-- <span class="text-body-1 me-6">{{ $T("filters") }}</span> -->
				<v-chip-group
					v-model="typeFilter"
					show-arrows
					center-active
					mandatory
					active-class="primary white--text"
					class="flex-grow-1 filter-chips"
				>
					<v-chip
						small
						:outlined="typeFilter !== ALL_TYPES"
						:value="ALL_TYPES"
						class="filter-chip"
					>
						{{ $T("all") }}
					</v-chip>
					<v-chip
						v-for="(type, index) in customersTypes"
						:key="index"
						small
						:outlined="typeFilter !== type.id"
						:value="type.id"
						class="filter-chip"
					>
						{{ type.name }}
					</v-chip>
				</v-chip-group>
			</v-card-text>

			<!-- date -->
			<v-card-text class="d-flex justify-space-between align-center pt-0">
				<!-- date dialog -->
				<v-dialog
					ref="dialog"
					v-model="modal"
					:return-value.sync="selectedMonth"
					persistent
					max-width="300px"
				>
					<!-- date text -->
					<template v-slot:activator="{ on, attrs }">
						<div
							v-bind="attrs"
							v-on="on"
							class="d-flex justify-space-between align-center primary--text"
						>
							<div class="d-flex align-center">
								<v-icon class="me-2" color="primary">mdi-calendar</v-icon>
								<span
									class="me-2"
									:class="$vuetify.breakpoint.xsOnly ? 'text-subtitle-1 font-weight-medium' : 'text-h5'"
								>{{ dateHeaderLabel }}</span>
								<span class="text-subtitle-2 align-self-end">{{
									moment(date, "YYYY-MM-DD").format("YYYY")
								}}</span>
							</div>
						</div>
					</template>
					<!-- dialog date picker -->
					<v-date-picker
						v-model="selectedMonth"
						full-width
						picker-date
						type="month"
						:max="
							new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
								.toISOString()
								.substr(0, 10)
						"
					>
						<v-spacer></v-spacer>
						<v-btn text color="primary" @click="modal = false">
							{{ $T("cancel") }}
						</v-btn>
						<v-btn
							text
							color="primary"
							@click="
								$refs.dialog.save(selectedMonth);
								date = moment(selectedMonth, 'YYYY-MM').format('YYYY-MM-DD');
								fetchVisits();
							"
						>
							{{ $T("ok") }}
						</v-btn>
					</v-date-picker>
				</v-dialog>

				<!-- today & daily report -->
				<div>
					<!-- today button -->
					<v-btn text small color="primary" @click="goToTodayDate">
						<!-- not translated -->
						{{ $T("components.today") }}
					</v-btn>

					<!-- daily report -->
					<v-btn
						v-if="date === moment().format('YYYY-MM-DD')"
						small
						icon
						color="primary"
						@click="dailyReportSheet = true"
					>
						<v-icon>mdi-file-document-edit-outline</v-icon>
					</v-btn>
				</div>
			</v-card-text>
		</v-card>

		<!-- days picker -->
		<!-- <v-month-days
			v-model="date"
			:month="selectedMonth"
			@input="fetchVisits(); ss()"
			:optional="false"
			@change="ss()"
			@click="ss()"
		/> -->

		<v-day-date
			v-model="date"
			:month="selectedMonth"
			@input="fetchVisits(); ss()"
			:optional="false"
			@change="ss()"
			@click="ss()"
		/>

		<v-container class="visit-card-container mx-auto">
			<!-- if (usePrices) {
							payments.oldVisitPaymentAmount =
								visit.amountReceived ||  getVisitTotal(visit.id);
						} -->
			<!-- {{ configurations.visitsConfig }} -->
			<div v-if="!loading && filteredVisits.length > 0">
				<visit-card
					v-for="visit in filteredVisits"
					:key="visit.id"
					:visit="visit"
					class="mb-5"
					:use-prices="usePrices"
					:is-pending="Boolean(visit.isPending)"
					@on-edit="(visitId) => $refs.VisitDialog.editVisit(visitId)"
					@on-edit-prevent="editPrevent"
					@on-invoice="(visitId) => previewInvoice(visitId)"
					@on-payment-updated="fetchVisits"
					@on-swipe-right="

						$refs.DeleteDialog.activeDialog(visit.id, 'visits');
					"
					@on-click="$refs.ViewVisitSheet.activeSheet(visit.id)"
					:disabled="disableCard"
				/>
			</div>
			<div
				v-else-if="!loading && filteredVisits.length === 0"
				class="d-flex flex-column justify-center align-center"
				style="height: 45vh"
			>
				<v-icon x-large class="mb-3">mdi-flask-remove</v-icon>
				<span class="grey--text text--darken-2">{{
					$T("no-visits-to-show")
				}}</span>
			</div>

			<!-- loader -->
			<loader v-if="loading" />
		</v-container>
	</div>
</template>

<script>
import DeleteDialog from '../components/DeleteDialog';
import Loader from '../components/Loader';
import VisitDialog from '../components/VisitDialog';
import VisitCard from '../components/VisitCard';
import ViewVisitSheet from '../components/ViewVisitSheet';
// import VMonthDays from '@/components/VMonthDays.vue';
import { dailyReportClassification } from '@/helpers/enums';
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import { useMaterialsSpecialtyVisits, usePrices, deletionExternal } from '@/configs/general.configs';
// import { disableDates } from '@/helpers/functions';
import CustomerDialog from '@/components/CustomerDialog.vue';
import VDayDate from '@/components/VDayDate.vue';
import AddVisitBottomSheet from '@/components/AddVisitBottomSheet.vue';
import RegionVisitsRateCard from '@/components/RegionVisitsRateCard.vue';

// chip-group value standing for "no type filter"; selectedFilter keeps using null for that
const ALL_TYPES = 'all';

export default {
	name: 'Visits',

	components: {
		DeleteDialog,
		Loader,
		CustomerDialog,
		VisitDialog,
		VisitCard,
		ViewVisitSheet,
		// VMonthDays,
		VDayDate,
		AddVisitBottomSheet,
		RegionVisitsRateCard
	},

	data() {
		return {
			usePrices,
			dailyReportClassification,
			deletionExternal,
			// disableDates,

			ALL_TYPES,

			modal: false,
			loading: true,
			isVisibleAdd: false,
			selectedFilter: null,
			selectedMonth: moment().format('YYYY-MM'),
			date: moment().format('YYYY-MM-DD'),
			disableCard: false,

			dailyReportLoading: false,
			dailyReportSheet: false,
			dailyReportData: {
				classification: dailyReportClassification.normalNote,
				report: null,
			},

			allowAdd: false,
		};
	},

	watch: {
		dailyReportSheet(val) {
			if (val) {
				this.dailyReportData.classification = this.dailyReport.id
					? this.dailyReport.classification
					: dailyReportClassification.normalNote;
				this.dailyReportData.report = this.dailyReport.report || null;
			}
		},

		date(val) {
			if (val) {
				// $vm.moment($vm.moment('2026-02-22').format('YYYY-MM-DD')).isSame($vm.moment().subtract(8, 'hour').format('YYYY-MM-DD'), 'day'
			}
		},
	},

	methods: {
		moment,

		ss() {
			this.allowAdd = this.moment(this.moment(this.date).format('YYYY-MM-DD')).isSame(this.moment(this.serverTime).subtract(this.lockVisitAfterDuration, 'hour').format('YYYY-MM-DD'), 'day')
		},

		fetchVisits() {
			// disabling visit card
			this.disableCard = true;

			// Active nav loading
			this.$emit('loading:active');
			this.$store
				.dispatch('visits/fetchAllData', { day: this.date })
				.finally(() => {
					this.$emit('loading:disable');
					this.disableCard = false;
				});
			this.$store.dispatch('config/fetchServerTime').then(() => {
				this.ss();
			});
		},

		editPrevent(message) {
			this.$eventBus.$emit('show-snackbar', message, 'info');
		},

		// display-only transform of a queued sync item into a visit-card-shaped object -
		// never mutates the queue item itself, since visitPayload is POSTed verbatim on sync
		buildPendingVisit(item) {
			const payload = item.visitPayload;
			const entity = this.getEntityById(payload.entityId) || { name: '-' };

			return {
				id: item.localId,
				isPending: true,
				queueStatus: item.status,
				entity: { ...entity, entityTypeId: entity.typeId },
				createdAt: payload.createdAt,
				notes: payload.notes,
				coreMessage: payload.coreMessage,
				nextVisitNotes: payload.nextVisitNotes,
				objections: payload.objections,
				purpose: payload.purpose,
				durationInMinutes: payload.durationInMinutes,
				residentDoctorNames: payload.residentDoctorNames,
				competitiveSamples: payload.competitiveSamples || [],
				otherSamples: payload.otherSamples,
				unavailableMaterials: (payload.unavailableMaterialIds || [])
					.map((id) => this.getMaterialById(id)?.name)
					.filter(Boolean),
				materialAmounts: payload.materialAmounts || [],
				payment: payload.payment,
				amountReceived: payload.amountReceived,
			};
		},

		previewInvoice(visitId) {
			if (!visitId) return;

			this.$router.push({
				name: 'visit-invoice-preview',
				params: { visitId },
			});
		},

		goToTodayDate() {
			const todayDate = moment().format('YYYY-MM-DD');
			if (this.date !== todayDate) {
				this.selectedMonth = moment().format('YYYY-MM');
				this.date = todayDate;
				this.fetchVisits();
			}
		},

		submitDailyReport() {
			this.dailyReportLoading = true;
			const actionName = !this.dailyReport.id ? 'create' : 'update';
			this.$store.dispatch(`dailyReport/${actionName}`, {
				id: this.dailyReport.id,
				classification: this.dailyReportData.classification,
				report: this.dailyReportData.report,
			}).then(() => {
				this.$eventBus.$emit('show-snackbar', this.$T('daily-report-is-updated'));
				this.$store.dispatch('dailyReport/fetchMine');
				this.dailyReportSheet = false;
			}).finally(() => {
				this.dailyReportLoading = false;
			});
		},

		// handling deleting
		getVisitTotal(id) {
			const visit = this.getVisitById(id);

			const samples = [];

			visit.materialAmounts.forEach((material) => {
				samples.push({
					amount: material.amount,
					price: !material.freeSample ? material.sellPrice : 0,
					discount: material.priceDiscount,
				});
			});

			return samples.reduce((res, c) => {
				const priceNet = c.amount * c.price;
				return res + (priceNet - (c.discount / 100) * priceNet);
			}, 0);
		},

		updateBalance(newAmount, oldVisitPaymentAmount) {
			if (!usePrices) return;
			this.$store.dispatch('profile/fetchBalance');
		},
	},

	computed: {
		// chip-group model: maps the 'all' chip onto selectedFilter === null
		typeFilter: {
			get() {
				return this.selectedFilter === null ? ALL_TYPES : this.selectedFilter;
			},
			set(value) {
				this.selectedFilter = value === ALL_TYPES ? null : value;
			},
		},

		dateHeaderLabel() {
			const isToday = this.date === moment().format('YYYY-MM-DD');
			const dayLabel = isToday ? this.$T('components.today') : moment(this.date, 'YYYY-MM-DD').format('dddd');

			return `${dayLabel}, ${moment(this.date, 'YYYY-MM-DD').format('DD MMMM')}`;
		},

		filteredVisits() {
			const merged = [...this.pendingVisits.map(this.buildPendingVisit), ...this.visits];
			if (this.selectedFilter !== null) {
				return merged.filter(
					(c) => c.entity.entityTypeId === this.selectedFilter
				);
			} else {
				return merged;
			}
		},

		// queued-offline visits still waiting to sync, filtered to the currently selected day
		// (the confirmed `visits` list is already day-filtered server-side via fetchVisits)
		pendingVisits() {
			return this.syncQueue.filter(
				(item) => moment(item.visitPayload.createdAt).format('YYYY-MM-DD') === this.date
			);
		},

		...mapState({
			balance: (state) => state.profile.balance,
			visits: (state) => state.visits.visits,
			syncQueue: (state) => state.sync.queue,
			specialtyMaterials: (state) => state.materials.specialtyMaterials,
			priceTypes: (state) => state.priceTypes.priceTypes,
			dailyReport: (state) => state.dailyReport.dailyReport,
			customersTypes: state => deletionExternal ? state.entities.customersTypes.filter(c => c.name !== 'Externals') : state.entities.customersTypes,
			configurations: state => state.configurations.configurations,
			// vacations: state => state.vacations.vacations,

			// staff: state => state.configurations.staff,
			// roleInStaff: state => state.configurations.roleInStaff,
			// customers: state => state.configurations.customers,
			// customersTypesC: state => state.configurations.customersTypes,
			// materialsFields: state => state.configurations.materialsFields,
			// visitsFields: state => state.configurations.visitsFields,

			// visitsConfig: state => state.configurations.configurations.visitsConfig,

			lockVisitAfterDuration: state => state.configurations.lockVisitAfterDuration,
			serverTime: state => state.configurations.serverTime,
			self: state => state.auth.self
		}),

		...mapGetters({
			getVisitById: 'visits/getVisitById',
			regionPlansStatisticsSummary: 'regionPlans/getRegionPlansStatisticsSummary',
			getEntityById: 'entities/getEntityById',
			getMaterialById: 'materials/getMaterialById',
		}),
	},

	mounted() {
		this.loading = true;
		Promise.all([
			this.$store.dispatch('configurations/fetchCurrentTime'),
			this.$store.dispatch('visits/fetchAllData', {
				day: moment().format('YYYY-MM-DD'),
			}),
			this.$store.dispatch('regions/fetchAll'),
			this.$store.dispatch('materials/fetchAll', {}),
			this.$store.dispatch('materialsTransactions/fetchAll'),
			this.$store.dispatch('materialForms/fetchAll'),
			this.$store.dispatch('dailyReport/fetchMine'),
			useMaterialsSpecialtyVisits && !this.specialtyMaterials.length
				? this.$store.dispatch('materials/fetchAllForSpecialtyVisits', {
					fromDate: moment().startOf('month').format('YYYY-MM-DD'),
					toDate: moment().endOf('month').format('YYYY-MM-DD'),
				})
				: null,
			!this.priceTypes.length ? this.$store.dispatch('priceTypes/fetchAll') : null,
			usePrices && !this.balance
				? this.$store.dispatch('profile/fetchBalance')
				: null,
			!this.customersTypes.length ? this.$store.dispatch('entities/fetchEntityType') : null,
			// !this.vacations.length ? this.$store.dispatch('vacations/fetchAll') : null
		]).finally(() => {
			setTimeout(() => {
				this.isVisibleAdd = true;
			}, 200);
			this.loading = false;
		});

		// secondary stat - kept out of the loading gate so a slow/failing response never blocks the page
		this.$store.dispatch('regionPlans/fetchStatistics', {
			fromDate: moment().format('YYYY-MM-DD'),
			toDate: moment().format('YYYY-MM-DD'),
			employeeIds: [this.self.employeeId],
		}).catch(() => {})
	},

	beforeDestroy() {
		this.$store.dispatch('visits/reset');
		this.$store.dispatch('regions/reset');
		this.$store.dispatch('materials/reset');
		this.$store.dispatch('materialForms/reset');
		this.$store.dispatch('regionPlans/resetStatistics');
	},
};
</script>

<style lang='scss' scoped>
.visit-card-container {
	overflow-x: hidden;
}

.add-fab {
	bottom: 60px !important;
}

.add-fab.sale-system-add-fab {
	bottom: 110px !important;
}

// type filter chips: light-outlined pills, solid primary when selected
.filter-chip.v-chip {
	height: 30px;
	padding: 0 18px;
	border-radius: 15px;
	font-size: 0.8125rem;
	font-weight: 500;
	letter-spacing: 0;
}

.filter-chip.v-chip--outlined {
	background-color: #fff !important;
	border: 1px solid #dfe1e5 !important;
	color: #3c4043;
}

.filter-chip.v-chip--active {
	border-color: transparent !important;

	// the group's active tint is a currentColor overlay; the solid primary fill
	// comes from active-class instead, so keep the overlay off
	&::before {
		opacity: 0;
	}
}

</style>
